import apiClient from './apiClient';
import {
  Quiz,
  QuizWithQuestions,
  QuizAttempt,
  QuizLeaderboardEntry,
  QuizResponse,
  CreateQuizData,
  UpdateQuizData,
  CreateQuizQuestionData,
  UpdateQuizQuestionData,
  QuizListResponse,
  AdminQuizListResponse,
  AttemptWithResponses,
  QuizQuestion,
} from '../types/quiz';

// ============================================
// Admin API Calls
// ============================================

/**
 * Create a new quiz (admin only)
 */
export const createQuiz = async (data: CreateQuizData): Promise<Quiz> => {
  const response = await apiClient.post('/api/quizzes/admin', data);
  return response.data;
};

/**
 * Get all quizzes (admin only - includes unpublished)
 */
export const getAllQuizzes = async (
  page = 1,
  limit = 20,
  includeUnpublished = true
): Promise<AdminQuizListResponse> => {
  const response = await apiClient.get('/api/quizzes/admin', {
    params: { page, limit, includeUnpublished },
  });
  return response.data;
};

/**
 * Update a quiz (admin only)
 */
export const updateQuiz = async (id: number, data: UpdateQuizData): Promise<Quiz> => {
  const response = await apiClient.put(`/api/quizzes/admin/${id}`, data);
  return response.data;
};

/**
 * Delete a quiz (admin only)
 */
export const deleteQuiz = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/quizzes/admin/${id}`);
};

/**
 * Publish a quiz (admin only)
 */
export const publishQuiz = async (id: number): Promise<Quiz> => {
  const response = await apiClient.post(`/api/quizzes/admin/${id}/publish`);
  return response.data;
};

/**
 * Unpublish a quiz (admin only)
 */
export const unpublishQuiz = async (id: number): Promise<Quiz> => {
  const response = await apiClient.post(`/api/quizzes/admin/${id}/unpublish`);
  return response.data;
};

/**
 * Add a question to a quiz (admin only)
 */
export const addQuestion = async (
  quizId: number,
  data: CreateQuizQuestionData
): Promise<QuizQuestion> => {
  const response = await apiClient.post(`/api/quizzes/admin/${quizId}/questions`, data);
  return response.data;
};

/**
 * Update a question (admin only)
 */
export const updateQuestion = async (
  quizId: number,
  questionId: number,
  data: UpdateQuizQuestionData
): Promise<QuizQuestion> => {
  // The admin API reads camelCase (see QuizController.updateQuestion). This used
  // to translate to snake_case, so every field except `options` was silently
  // dropped and the options themselves were rewritten as undefined.
  const response = await apiClient.put(
    `/api/quizzes/admin/questions/${questionId}`,
    data
  );
  return response.data;
};

/**
 * Delete a question (admin only)
 */
export const deleteQuestion = async (quizId: number, questionId: number): Promise<void> => {
  await apiClient.delete(`/api/quizzes/admin/questions/${questionId}`);
};

/**
 * Reorder questions in a quiz (admin only)
 */
export const reorderQuestions = async (
  quizId: number,
  questionIds: number[]
): Promise<void> => {
  await apiClient.put(`/api/quizzes/${quizId}/questions/reorder`, { questionIds });
};

// ============================================
// Student API Calls
// ============================================

/**
 * Get all published quizzes (student)
 */
export const getPublishedQuizzes = async (
  page = 1,
  limit = 20
): Promise<QuizListResponse> => {
  const response = await apiClient.get('/api/quizzes', {
    params: { page, limit },
  });
  return response.data;
};

/**
 * Get a quiz with all questions and options (student view)
 */
export const getQuiz = async (id: number): Promise<QuizWithQuestions> => {
  const response = await apiClient.get(`/api/quizzes/${id}`);
  return response.data;
};

/**
 * Get a quiz with all questions and options (admin view)
 */
export const getAdminQuiz = async (id: number): Promise<QuizWithQuestions> => {
  const response = await apiClient.get(`/api/quizzes/admin/${id}`);
  return response.data;
};

/**
 * Start a quiz attempt (student)
 */
export const startQuizAttempt = async (quizId: number): Promise<QuizAttempt> => {
  const response = await apiClient.post(`/api/quizzes/${quizId}/start`);
  return response.data;
};

/**
 * Submit an answer to a question (student)
 */
export const submitAnswer = async (
  quizId: number,
  attemptId: number,
  questionId: number,
  optionId: number,
  responseTimeSeconds: number
): Promise<QuizResponse> => {
  const response = await apiClient.post(`/api/quizzes/attempts/${attemptId}/answer`, {
    question_id: questionId,
    option_id: optionId,
    response_time_seconds: responseTimeSeconds,
  });
  return response.data;
};

/**
 * Complete a quiz attempt (student)
 */
export const completeQuizAttempt = async (
  quizId: number,
  attemptId: number
): Promise<QuizAttempt> => {
  const response = await apiClient.post(`/api/quizzes/attempts/${attemptId}/complete`);
  return response.data;
};

/**
 * Get quiz leaderboard (student/admin)
 */
export const getQuizLeaderboard = async (
  quizId: number,
  limit = 100
): Promise<QuizLeaderboardEntry[]> => {
  const response = await apiClient.get(`/api/quizzes/${quizId}/leaderboard`, {
    params: { limit },
  });
  return response.data;
};

/**
 * Get the current user's attempt for a quiz (student)
 */
export const getMyAttempt = async (quizId: number): Promise<QuizAttempt | null> => {
  try {
    const response = await apiClient.get(`/api/quizzes/${quizId}/my-attempt`);
    console.log('[getMyAttempt] Raw response data:', JSON.stringify(response.data, null, 2));

    if (response.data?.attempt) {
      console.log('[getMyAttempt] Attempt from response:', JSON.stringify(response.data.attempt, null, 2));
      return response.data.attempt;
    }

    console.log('[getMyAttempt] Returning response.data directly:', JSON.stringify(response.data, null, 2));
    return response.data;
  } catch (error) {
    // 404 means no attempt exists yet, which is fine
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      console.log('[getMyAttempt] No attempt found (404), returning null');
      return null;
    }
    // Re-throw other errors
    throw error;
  }
};

/**
 * Get the current user's attempt with all responses (student)
 */
export const getMyAttemptWithResponses = async (
  quizId: number
): Promise<AttemptWithResponses | null> => {
  try {
    const response = await apiClient.get(`/api/quizzes/${quizId}/my-attempt`);
    return response.data;
  } catch (error) {
    // 404 means no attempt exists yet, which is fine
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
};
